import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Award, ShieldCheck, Clock, Users, Heart, MapPin, ArrowRight } from "lucide-react";
import { companyInfo as mockCompany, whyChooseUs as mockWhyChooseUs, galleryImages } from "../data/mock";
import { useCompany } from "../hooks/useContent";
import PageHero from "../components/PageHero";

export default function AboutPage() {
  const { data: companyData } = useCompany();
  const companyInfo = companyData.companyInfo;
  const whyChooseUs = companyData.whyChooseUs;

  return (
    <div>
      <Helmet>
        <title>Despre Noi – Casa Funerară Sfântul Andrei Constanța</title>
        <meta name="description" content="Aflați povestea Casei Funerare Sfântul Andrei – 10 ani de experiență, vehicule funerare autorizate și servicii complete în Constanța și Lumina. NON-STOP 24/7." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/despre-noi" />
        <meta property="og:title" content="Despre Noi – Casa Funerară Sfântul Andrei Constanța" />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/despre-noi" />
      </Helmet>
      <PageHero
        image={galleryImages.sfAndreiBiserica}
        title="Despre Casa Funerară Sfântul Andrei"
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Despre Noi" },
        ]}
      />

      {/* Story Section */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>
                Povestea Noastră
              </span>
              <h2 className="text-2xl sm:text-3xl font-semibold mt-4 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                Un Deceniu de Profesionalism și Empatie
              </h2>
              <div className="space-y-5 text-[#666666] leading-relaxed">
                <p>
                  Casa Funerară Sfântul Andrei a fost înființată în 2019 în Constanța, cu misiunea de a oferi servicii funerare de cea mai înaltă calitate, într-un moment în care familiile au cea mai mare nevoie de sprijin.
                </p>
                <p>
                  Cu o echipă cu experiență cumulată de peste 10 ani în domeniul serviciilor funerare, am crescut organic, prin încrederea familiilor care ne-au ales și ne-au recomandat mai departe. Astăzi, suntem prezenți atât în Constanța, cât și în Comuna Lumina, cu două sedii permanente, disponibile NON-STOP.
                </p>
                <p>
                  Credem că fiecare persoană merită o despărțire demnă, iar fiecare familie merită sprijin real – nu doar servicii. De aceea, abordarea noastră combină profesionalismul desăvârșit cu empatia autentică, tratând fiecare situație cu respectul pe care îl merită.
                </p>
              </div>
            </div>
            <div className="rounded-2xl p-10 lg:p-12" style={{ backgroundColor: "var(--gold-bg)" }}>
              <h3
                className="text-xl font-semibold mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Valorile Noastre
              </h3>
              <div className="space-y-8">
                {[
                  { icon: Heart, title: "Empatie", desc: "Tratăm fiecare familie cu compasiune și înțelegere profundă." },
                  { icon: Award, title: "Profesionalism", desc: "Standarde ridicate în fiecare serviciu pe care îl oferim." },
                  { icon: ShieldCheck, title: "Încredere", desc: "Transparență totală în prețuri, procese și comunicare." },
                  { icon: Clock, title: "Disponibilitate", desc: "NON-STOP 24/7, pentru că nevoile nu respectă un program." },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: "var(--gold-bg)" }}>
                      <item.icon className="w-4 h-4" style={{ color: "var(--gold)" }} />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0f0f0f] mb-1">{item.title}</h4>
                      <p className="text-sm text-[#999999]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16" style={{ backgroundColor: "var(--gold)", background: "linear-gradient(135deg, #A8924A, #C6A85A)" }}>
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "2019", label: "Anul Înființării" },
              { value: "10+", label: "Ani Experiență" },
              { value: "2", label: "Sedii Permanente" },
              { value: "24/7", label: "Disponibilitate" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p
                  className="text-3xl sm:text-4xl font-semibold text-white mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-[2px] text-white/50">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet & Services Gallery */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[4px] font-medium" style={{ color: "var(--gold)" }}>
              Flota Noastră
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mt-4 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Vehicule Autorizate la Standarde Europene
            </h2>
            <p className="text-sm text-[#666666] leading-relaxed">
              Dispunem de vehicule funerare autorizate, menținute la cele mai înalte standarde,
              pentru transport local, național și internațional.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="img-hover-zoom overflow-hidden aspect-[16/10]">
              <img
                src={galleryImages.van}
                alt="Vehicul funerar autorizat – Casa Funerară Sfântul Andrei Constanța"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[16/10]">
              <img
                src={galleryImages.mercedes}
                alt="Mașină funerară – transport demnitar Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[16/10]">
              <img
                src={galleryImages.opelVanFront}
                alt="Vehicul funerar Opel autorizat – Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[16/10]">
              <img
                src={galleryImages.vanBranding}
                alt="Servicii Funerare Complete NON STOP – Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Church Collaboration */}
      <section className="py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="img-hover-zoom overflow-hidden aspect-square lg:aspect-auto">
            <img
              src={galleryImages.churchInterior}
              alt="Interior biserică ortodoxă – colaborare cu toate bisericile din Constanța"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="flex items-center bg-[#FAFAF8] p-10 lg:p-16">
            <div>
              <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
                Parteneriate
              </span>
              <h2
                className="text-2xl sm:text-3xl font-semibold mt-4 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Colaborare cu Toate Bisericile
              </h2>
              <p className="text-sm text-[#666666] leading-relaxed mb-6">
                Casa Funerară Sfântul Andrei colaborează strâns cu toate bisericile și cimitirele
                din Constanța și împrejurimi. Cunoaștem tradițiile și cerințele fiecărei parohii,
                asigurând că fiecare ceremonie se desfășoară în deplină armonie cu valorile
                spirituale ale familiei.
              </p>
              <div className="space-y-3">
                {["Toate bisericile ortodoxe din Constanța", "Cimitirele din Constanța și Lumina", "Slujbe de înmormântare și parastas", "Respect deplin pentru tradiții"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#0f0f0f] flex-shrink-0" />
                    <span className="text-sm text-[#666666]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Detailed */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
              Avantaje
            </span>
            <h2
              className="text-3xl sm:text-4xl font-semibold mt-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              De Ce Familiile Ne Aleg
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {whyChooseUs.map((item, i) => (
              <div key={i} className="flex items-start gap-5 group">
                <div className="w-8 h-8 flex items-center justify-center bg-[#FAFAF8] border border-[#E8E4DC] flex-shrink-0 group-hover:bg-[#0f0f0f] group-hover:border-[#0f0f0f] transition-all duration-400">
                  <span className="text-xs font-semibold text-[#999999] group-hover:text-white transition-colors">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0f0f0f] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#999999] leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="section-spacing bg-[#FAFAF8]">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
              Locații
            </span>
            <h2
              className="text-3xl sm:text-4xl font-semibold mt-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sediile Noastre
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockCompany.addresses.map((addr, i) => (
              <div key={i} className="bg-white p-8 border border-[#E8E4DC]">
                <div className="flex items-start gap-4 mb-6">
                  <MapPin className="w-5 h-5 text-[#999999] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#0f0f0f] mb-1">{addr.name}</h3>
                    <p className="text-sm text-[#666666]">
                      {addr.street}{addr.postalCode ? `, ${addr.postalCode}` : ''} {addr.city}
                    </p>
                    <p className="text-xs text-[#999999] mt-2">Program: NON-STOP 24/7</p>
                  </div>
                </div>
                <div className="map-container">
                  <iframe
                    src={addr.mapEmbed}
                    title={addr.name}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Suntem Aici pentru Dumneavoastră
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Disponibili NON-STOP. Sunați-ne oricând aveți nevoie de sprijin.
          </p>
          <a
            href={`tel:${mockCompany.phone}`}
            className="inline-flex items-center gap-3 bg-white text-[#0f0f0f] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
          >
            <Phone className="w-4 h-4" />
            {mockCompany.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
